import { useRef, useMemo, useCallback, useContext, useEffect, useState } from 'react';
import get from 'transmute/get';
import throttle from 'hs-lodash/throttle';
import onboardingContext from 'content-assistance-lib/contexts/onboardingContext';
import ProsemirrorEditor from 'hubspot-prosemirror/prosemirror/ProsemirrorEditor';
import { convertToHtml, convertToPlaintext, getEditorStateFromHtml, focusEditor } from 'hubspot-prosemirror/prosemirror/ProsemirrorUtils';
import styled from 'foundations-theming/utils';
import useHubSpotRTE from 'hubspot-prosemirror/prosemirror/useHubSpotRTE';
import { AT_MENTIONS, INSERT_CORE, RICH_TEXT, PLAIN_TEXT, CONTENT_ASSISTANT_SLASH_COMMAND } from 'conversations-thread-data/channel-configuration/public/constants';
import { BOLD, GLOBAL_VARS, ITALIC } from 'hubspot-prosemirror/prosemirror/editorCapabilities';
import { isProsemirrorState } from '../operators/isProsemirror';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module '../.... Remove this comment to see the full error message
import PublishButton from '../plugins/PublishButton';
import usePrevious from 'react-utils/hooks/usePrevious';
import { getPlaceholderPlugin } from 'hubspot-prosemirror/prosemirror/plugins/PlaceholderPlugin';
import { configureMentionsPlugin } from '../plugins/mentionsPlugin';
import { configureSnippetsPlugin } from '../plugins/snippetsPlugin';
import { convertInboxConfigToProsemirror } from '../operators/convertInboxConfigToProsemirror';
import { getPlaceholderText } from '../../../editor-utilities/protected/utils';
import { useContentAssistanceSettings } from 'conversations-thread-data/content-assistance/public/hooks/useContentAssistanceSettings';
import { useShouldAutoFocusEditor } from '../hooks/useShouldAutoFocusEditor';
import { mergeProsemirrorConfigs } from '../operators/mergeProsemirrorConfigs';
import { EditorTrackerContext } from '../../../usage-tracking/context/EditorTrackerContext';
// TODO: move to composer-ui
import { ChannelCapabilitiesConfigContext } from 'conversations-thread-view/channel-capabilities/context/ChannelCapabilitiesConfigContext';
import { useStateReactionTime } from '../hooks/useStateReactionTime';
import { insertableReplyHistoryPluginKey, helpers as insertableReplyHistoryHelpers, commands as insertableReplyHistoryCommands } from 'hubspot-prosemirror/prosemirror/plugins/InsertableReplyHistoryPlugin';
import { useAtMentionCapabilityData } from 'conversations-thread-data/channel-capabilities/public/hooks';
import { useRichTextEditorMetrics } from '../../../metrics/protected/hooks';
import { Metrics } from '../../../metrics/protected/Metrics';
import { jsx as _jsx } from "react/jsx-runtime";
export const prosemirrorMenuClassName = 'ProsemirrorMenu__inbox';
export const prosemirrorInputClassName = 'ProsemirrorInbox__inbox';
const ProsemirrorStyledEditor = styled.div.withConfig({
  displayName: "ProsemirrorRichTextEditor__ProsemirrorStyledEditor",
  componentId: "sc-1bhvgo5-0"
})(["flex-grow:1;position:relative;display:flex;flex-direction:column;align-items:stretch;"]);
function ProsemirrorRichTextEditor({
  renderTray: attachmentTray,
  renderSubmitButton,
  disableSubmitMessage,
  disabledAwaitingUserAction,
  onSubmit,
  className,
  setEditorState: onStateChange,
  capabilitiesConfig,
  handleAttachmentChange,
  fileUploadsInProgCount,
  shouldDisableSubmit,
  uploadFile,
  prosemirrorConfig,
  placeholder,
  attachments,
  uploadDroppedFiles,
  editorState: initialEditorState,
  auth,
  onTyping,
  onInsert,
  __typingThrottleInMs = 3000,
  disabledConfig,
  internalEditorStateSetterRef,
  editorRef,
  plugins,
  initialHtml = '',
  handleFirstClick
}) {
  const {
    editorType,
    markAndMeasureRenderDuration
  } = useRichTextEditorMetrics();
  useEffect(() => {
    const timeSinceFirstMount = markAndMeasureRenderDuration();
    if (timeSinceFirstMount) {
      const isFirstRender = performance.getEntriesByName('editor-render').length === 0;
      Metrics.timer('editor-render', {
        type: editorType,
        isFirstRender: `${isFirstRender}`
      }).update(timeSinceFirstMount);
      if (isFirstRender) {
        performance.mark('editor-render');
      }
    }
  }, [editorType, markAndMeasureRenderDuration]);
  const {
    shouldShowAIOnboardingPopover,
    setHasSeenAIOnboardingPopover
  } = useContext(onboardingContext);
  const {
    trackEvent
  } = useContext(EditorTrackerContext);
  const autoFocusOnMount = useShouldAutoFocusEditor();
  const submitButtonRef = useRef(null);
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'customerAnalysisEnabled' does not exist ... Remove this comment to see the full error message
  const {
    customerAnalysisEnabled,
    contentAssistanceEnabled
  } = useContentAssistanceSettings();
  const atMentionsConfig = capabilitiesConfig[AT_MENTIONS];
  const atMentionData = useAtMentionCapabilityData({
    inboxId: atMentionsConfig === null || atMentionsConfig === void 0 ? void 0 : atMentionsConfig.inboxId
  });
  const usePlaintextMode = !!capabilitiesConfig[PLAIN_TEXT] || get('usePlaintextMode', prosemirrorConfig[GLOBAL_VARS]);
  const inboxConfigAllowsBasicFormatting = capabilitiesConfig[RICH_TEXT] && !capabilitiesConfig[RICH_TEXT].formatStyles;
  const prosemirrorConfigAllowsBasicFormatting = prosemirrorConfig[BOLD] && prosemirrorConfig[ITALIC];
  const allowBasicFormatting = inboxConfigAllowsBasicFormatting || prosemirrorConfigAllowsBasicFormatting;
  const snippetProps = useMemo(() => capabilitiesConfig[INSERT_CORE] ? {
    contactEmail: capabilitiesConfig[INSERT_CORE].contactEmail,
    contactVid: capabilitiesConfig[INSERT_CORE].contactVid,
    supplementalObjects: capabilitiesConfig[INSERT_CORE].supplementalObjects,
    usePlaintextMode,
    plaintextOptions: usePlaintextMode ? {
      allowBasicFormatting
    } : undefined
  } : {}, [capabilitiesConfig, usePlaintextMode, allowBasicFormatting]);
  const [customPluginConfig, setCustomPluginConfig] = useState(() => {
    return Object.assign({
      mentions: configureMentionsPlugin(atMentionData),
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ snippetFunctions: any; contact... Remove this comment to see the full error message
      snippets: configureSnippetsPlugin(Object.assign({}, snippetProps))
    }, placeholder ? {
      placeholder: getPlaceholderPlugin(getPlaceholderText(placeholder.props.placeholder, !!capabilitiesConfig[CONTENT_ASSISTANT_SLASH_COMMAND]))
    } : {});
  });
  useEffect(() => {
    setCustomPluginConfig(prevConfig => {
      return Object.assign({}, prevConfig, {
        mentions: configureMentionsPlugin(atMentionData)
      });
    });
  }, [atMentionData]);
  const prevSnippetProps = usePrevious(snippetProps);
  useEffect(() => {
    if (snippetProps !== prevSnippetProps) {
      setCustomPluginConfig(Object.assign({}, customPluginConfig, {
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ snippetFunctions: any; contact... Remove this comment to see the full error message
        snippets: configureSnippetsPlugin(Object.assign({}, snippetProps))
      }));
    }
  }, [snippetProps, prevSnippetProps, setCustomPluginConfig, customPluginConfig]);
  const customPlugins = useMemo(() => {
    const tempPluginConfig = customPluginConfig;
    if (!capabilitiesConfig[AT_MENTIONS]) {
      // @ts-expect-error ts-migrate(2790) FIXME: The operand of a 'delete' operator must be optiona... Remove this comment to see the full error message
      delete tempPluginConfig.mentions;
    }
    return Object.values(tempPluginConfig).concat(...plugins);
  }, [customPluginConfig, capabilitiesConfig, plugins]);
  const [editorState, setEditorState] = useHubSpotRTE();
  if (internalEditorStateSetterRef) {
    internalEditorStateSetterRef.current = setEditorState;
  }
  useEffect(() => {
    if (initialEditorState && isProsemirrorState(initialEditorState) && autoFocusOnMount) {
      setEditorState(focusEditor(initialEditorState));
      onStateChange(initialEditorState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleTyping = useMemo(() => {
    return throttle(onTyping, __typingThrottleInMs, {
      leading: true,
      trailing: false
    });
  }, [onTyping, __typingThrottleInMs]);
  const handleSubmit = useCallback((e, options = {}) => {
    e.preventDefault();
    e.stopPropagation();
    handleTyping.cancel();

    // Before submitting, we may have to elide the reply history if it's
    // unmodified. The backend will fill in the reply history content, so
    // we only need to include it if the user has modified the history.
    const isUsingInsertableReplyHistoryPlugin = Boolean(insertableReplyHistoryPluginKey.getState(editorState));
    const wereRepliesAlreadyInserted = isUsingInsertableReplyHistoryPlugin ? insertableReplyHistoryHelpers.areRepliesAlreadyInserted(editorState) : false;
    const haveRepliesBeenModified = isUsingInsertableReplyHistoryPlugin ? insertableReplyHistoryHelpers.haveRepliesBeenModified(editorState) : false;
    const isReplyNodePresent = isUsingInsertableReplyHistoryPlugin ? insertableReplyHistoryHelpers.editorHasReplies(editorState) : false;

    // If replies were inserted but later deleted, we interpret this as
    // a user intent to send no reply history with the message. We will
    // use the local reply history in this case, which will be empty.
    const repliesInsertedThenDeleted = wereRepliesAlreadyInserted && !isReplyNodePresent;
    const shouldUseLocalReplies = haveRepliesBeenModified || repliesInsertedThenDeleted;
    const shouldUseBackendReplies = !shouldUseLocalReplies;
    if (isUsingInsertableReplyHistoryPlugin && shouldUseBackendReplies) {
      if (isReplyNodePresent) {
        // Delete the reply history and use a dispatch function to submit the patched editor state.
        insertableReplyHistoryCommands.deleteReplies(editorState, useBackendRepliesDispatch);
      } else {
        // The reply history was not found, so there's nothing to change.
        performSubmit(editorState);
      }
    } else if (isUsingInsertableReplyHistoryPlugin && shouldUseLocalReplies) {
      // We can use the whole editor state and indicate that the reply history is already included.
      performSubmit(editorState, {
        alreadyIncludesPreviousReplies: true
      });
    } else {
      // We are not using the plugin, so submit the editor state without options.
      performSubmit(editorState);
    }

    // Use a patched dispatch function to produce a new editor state
    // instead of updating the editor component.
    function useBackendRepliesDispatch(tr) {
      performSubmit(editorState.apply(tr));
    }

    // Submit the editor state, with extra options if provided.
    function performSubmit(stateToSubmit, extraOptions = {}) {
      const plainText = convertToPlaintext(stateToSubmit).trim();
      const html = convertToHtml(stateToSubmit) || plainText;
      onSubmit({
        editorState: stateToSubmit,
        html,
        plainText,
        options: Object.assign({}, options, extraOptions)
      });
      setEditorState(getEditorStateFromHtml(initialHtml));
    }
  }, [handleTyping, editorState, onSubmit, setEditorState, initialHtml]);
  const customSubmitButton = useCallback(() => /*#__PURE__*/_jsx(PublishButton, {
    disableSubmit: shouldDisableSubmit,
    disableSubmitMessage: disableSubmitMessage,
    disabledAwaitingUserAction: disabledAwaitingUserAction,
    onSubmit: handleSubmit,
    buttonRef: submitButtonRef,
    renderSubmitButton: renderSubmitButton
  }), [disableSubmitMessage, disabledAwaitingUserAction, submitButtonRef, handleSubmit, renderSubmitButton, shouldDisableSubmit]);
  const {
    originalGenericChannelId
  } = useContext(ChannelCapabilitiesConfigContext);
  const prosemirrorConfigFromInboxConfig = useMemo(() => {
    return convertInboxConfigToProsemirror({
      capabilitiesConfig,
      handleAttachmentChange,
      submitButtonRef,
      uploadFile,
      attachments,
      fileUploadsInProgCount,
      uploadDroppedFiles,
      auth,
      onInsert,
      customerAnalysisEnabled,
      contentAssistanceEnabled,
      shouldShowAIOnboardingPopover,
      setHasSeenAIOnboardingPopover,
      trackEvent,
      originalGenericChannelId
    });
  }, [capabilitiesConfig, handleAttachmentChange, uploadFile, attachments, fileUploadsInProgCount, uploadDroppedFiles, auth, onInsert, customerAnalysisEnabled, contentAssistanceEnabled, shouldShowAIOnboardingPopover, setHasSeenAIOnboardingPopover, trackEvent, originalGenericChannelId]);
  const editorConfig = useMemo(() => mergeProsemirrorConfigs(prosemirrorConfigFromInboxConfig, prosemirrorConfig), [prosemirrorConfig, prosemirrorConfigFromInboxConfig]);
  const {
    editorRef: editorWrapperRef,
    trackReactionTimeOnChange
  } = useStateReactionTime();
  const handleEditorStateChange = useCallback(newEditorState => {
    setEditorState(newEditorState);
    onStateChange(newEditorState);
    trackReactionTimeOnChange();
    if (!editorState.doc.eq(newEditorState.doc)) {
      handleTyping();
    }
  }, [onStateChange, editorState, setEditorState, handleTyping, trackReactionTimeOnChange]);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized keeping className
    _jsx(ProsemirrorStyledEditor, {
      ref: editorWrapperRef,
      className: `${className} ProsemirrorStyledEditor`,
      "data-test-id": "composer-editor",
      onClick: () => {
        handleFirstClick === null || handleFirstClick === void 0 || handleFirstClick('ProsemirrorRichTextEditor');
      },
      children: /*#__PURE__*/_jsx(ProsemirrorEditor, {
        ref: editorRef,
        auth: auth,
        editorConfig: editorConfig,
        editorState: editorState,
        setEditorState: handleEditorStateChange,
        autoFocus: autoFocusOnMount,
        customPlugins: customPlugins,
        menuClassName: prosemirrorMenuClassName,
        inputClassName: prosemirrorInputClassName,
        attachmentTray: attachmentTray,
        submitButton: customSubmitButton,
        performanceTrackingConfig: {
          instanceKey: `Inbox-Reply-Editor`
        },
        disabledConfig: disabledConfig
      })
    })
  );
}
ProsemirrorRichTextEditor.displayName = 'ProsemirrorRichTextEditor';
export default ProsemirrorRichTextEditor;